import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import { useVersionStatus } from '../../../src/hooks/useVersionStatus';
import type { Theme } from '../../../src/theme/types';
import ScreenHeader from '../../../src/components/ScreenHeader';

const STORE_NAME = Platform.OS === 'ios' ? 'App Store' : 'Play Store';

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { padding: 16, paddingBottom: 40, gap: 24 },
  card: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    overflow: 'hidden' as const,
    ...theme.shadows.sm,
  },
  cardContent: { padding: 16 },
  descriptionText: {
    color: theme.colors.textPrimary,
    fontSize: theme.fs(14),
    lineHeight: theme.fs(22),
  },
  versionText: {
    fontSize: theme.fs(13),
    color: theme.colors.borderMedium,
    fontWeight: '500' as const,
    textAlign: 'center' as const,
    marginTop: 24,
  },
  statusText: {
    fontSize: theme.fs(13),
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginTop: 6,
  },
  updateText: {
    fontSize: theme.fs(13),
    color: theme.colors.primary,
    fontWeight: '600' as const,
    textAlign: 'center' as const,
    marginTop: 6,
  },
});

export default function AboutScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();
  const { version, status } = useVersionStatus();

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="About Cynosure"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'about.back-button',
        }}
      />
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.card}>
          <View style={styles.cardContent}>
            <Text style={styles.descriptionText}>
              Time is one of the most valuable resources we have. Cynosure helps you define your vision, organize projects, track activities, and build lasting habits — guiding you toward your north star. By understanding how you spend your time and investing consistent effort in the right habits, you achieve meaningful, sustained growth.
            </Text>
            <Text style={[styles.descriptionText, { marginTop: 16 }]}>
              The word "cynosure" comes from the Greek for "dog's tail," referring to the constellation Ursa Minor and its brightest star, Polaris — the North Star. For centuries, travelers used it as a fixed point of guidance. In the same way, Cynosure is designed to be your personal north star — a steady reference point that keeps you oriented toward what matters most as you navigate your days.
            </Text>
          </View>
        </View>

        <Text style={styles.versionText} testID="about.version">
          Cynosure v{version}
        </Text>

        {/* 'unknown' renders nothing at all — see useVersionStatus on why a
            missing answer must not become a claim. */}
        {status === 'current' && <Text style={styles.statusText}>Up to date</Text>}
        {status === 'outdated' &&
          (Platform.OS === 'web' ? (
            <TouchableOpacity
              onPress={() => window.location.reload()}
              accessibilityRole="button"
              accessibilityLabel="Reload to update"
              testID="about.reload-button"
            >
              <Text style={styles.updateText}>A newer version is available — Reload</Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.updateText}>
              A newer version is available in the {STORE_NAME}.
            </Text>
          ))}
      </ScrollView>
    </View>
  );
}
